import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Wallet, Copy, ExternalLink, Loader2, TrendingUp } from 'lucide-react';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const _fmt = (amount, currency) => {
  const ccy = (currency || 'GBP').toUpperCase();
  const sym = ccy === 'INR' ? '₹' : ccy === 'CAD' ? 'C$' : '£';
  const value = ccy === 'INR'
    ? Number(amount || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })
    : Number(amount || 0).toFixed(2);
  return `${sym}${value}`;
};

/**
 * ProAffiliateWallet — shown on the Professional Dashboard.
 *
 * Reads /api/pros/wallet (aggregates `affiliate_payouts` by status for
 * the current user) and surfaces the referral link they should share.
 * 20% commission on every unlock fee paid by a buyer they referred.
 */
const ProAffiliateWallet = ({ dataTestId = 'pro-affiliate-wallet' }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/pros/wallet`);
        if (!cancelled) setData(res.data);
      } catch {
        // Silent — wallet only loads for authed pros.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const copy = () => {
    if (!data?.referral_url) return;
    navigator.clipboard.writeText(data.referral_url);
    toast.success('Referral link copied');
  };

  if (loading) {
    return (
      <div className="bg-white border border-slate-200 rounded-md p-5 mb-6 flex items-center gap-3" data-testid={`${dataTestId}-loading`}>
        <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
        <span className="text-sm text-slate-500">Loading affiliate wallet…</span>
      </div>
    );
  }
  if (!data?.referral_code) return null;

  const owed = data.owed?.amount || 0;
  const paid = data.paid?.amount || 0;
  const lifetime = data.lifetime_earned || 0;

  return (
    <div className="bg-gradient-to-br from-[#0F172A] to-slate-800 text-white rounded-md p-5 sm:p-6 mb-6" data-testid={dataTestId}>
      <div className="flex items-start gap-3 mb-4">
        <div className="bg-[#E07A3A]/20 border border-[#E07A3A]/40 p-2 rounded flex-shrink-0">
          <Wallet className="h-5 w-5 text-[#E07A3A]" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] tracking-[0.3em] text-[#E07A3A] uppercase font-semibold">Affiliate Wallet · 20% Commission</p>
          <h3 className="font-['Playfair_Display'] text-xl mt-1 text-white">Earn on every buyer you bring</h3>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-5">
        <div className="bg-white/5 rounded p-3" data-testid={`${dataTestId}-lifetime`}>
          <p className="text-[10px] uppercase tracking-wider text-slate-300 mb-1">Lifetime</p>
          <p className="text-xl font-['Playfair_Display'] text-white">{_fmt(lifetime, data.owed?.currency || data.paid?.currency)}</p>
        </div>
        <div className="bg-white/5 rounded p-3" data-testid={`${dataTestId}-owed`}>
          <p className="text-[10px] uppercase tracking-wider text-amber-300 mb-1">Pending</p>
          <p className="text-xl font-['Playfair_Display'] text-white">{_fmt(owed, data.owed?.currency)}</p>
        </div>
        <div className="bg-white/5 rounded p-3" data-testid={`${dataTestId}-paid`}>
          <p className="text-[10px] uppercase tracking-wider text-emerald-300 mb-1">Paid out</p>
          <p className="text-xl font-['Playfair_Display'] text-white">{_fmt(paid, data.paid?.currency)}</p>
        </div>
      </div>

      <div className="bg-white/5 border border-white/10 rounded p-3 mb-3">
        <p className="text-[10px] uppercase tracking-wider text-slate-300 mb-1">Your referral link</p>
        <div className="flex items-center gap-2">
          <code className="flex-1 text-xs sm:text-sm font-mono truncate text-white/90" data-testid={`${dataTestId}-link`}>
            {data.referral_url}
          </code>
          <Button size="sm" variant="ghost" onClick={copy} className="text-white hover:bg-white/10 flex-shrink-0" data-testid={`${dataTestId}-copy`}>
            <Copy className="h-3.5 w-3.5" />
          </Button>
          <a
            href={data.referral_url}
            target="_blank" rel="noopener noreferrer"
            className="text-white/70 hover:text-white flex-shrink-0"
            data-testid={`${dataTestId}-open`}
          >
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </div>
      </div>

      {(data.recent || []).length > 0 && (
        <div className="text-xs text-slate-300">
          <p className="flex items-center gap-1 mb-2">
            <TrendingUp className="h-3 w-3" /> Recent commissions
          </p>
          <ul className="space-y-1" data-testid={`${dataTestId}-recent`}>
            {data.recent.slice(0, 3).map((r, i) => (
              <li key={i} className="flex justify-between text-[11px] text-white/80">
                <span className="truncate">{r.label || `${r.purpose} · ${r.referee_email || 'buyer'}`}</span>
                <span className="font-semibold text-emerald-300">{_fmt(r.amount, r.currency)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default ProAffiliateWallet;
